// Çalışan Yönetimi tablosunun sütunları ve hücre biçimlendirme yardımcıları.

// `label`: Turkish fallback; `labelKey`: the text in the current language.
const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Evet', labelKey: 'employees.yes' },
  { value: 'No', label: 'Hayır', labelKey: 'employees.no' },
];

// Sıra tablodaki sütun sırasıdır. `width`: varsayılan sütun genişliği (px);
// kullanıcı sürükleyerek değiştirebilir.
export const EMPLOYEE_COLUMNS = {
  eName: { label: 'Ad', type: 'text', width: 190 },
  eSurname: { label: 'Soyad', type: 'text', width: 150 },
  tcNumber: { label: 'TC No', type: 'text', width: 140 },
  phone: { label: 'Telefon', type: 'text', width: 150 },
  eMail: { label: 'E-posta', type: 'text', width: 230 },
  departman: { label: 'Departman', type: 'text', width: 180 },
  profession: { label: 'Meslek', type: 'text', width: 170 },
  workType: {
    label: 'Çalışma Şekli',
    type: 'select',
    width: 150,
    options: [
      { value: 'Full Time', label: 'Tam Zamanlı', labelKey: 'employees.options.fullTime' },
      { value: 'Part Time', label: 'Yarı Zamanlı', labelKey: 'employees.options.partTime' },
    ],
  },
  startDate: { label: 'Başlangıç Tarihi', type: 'date', width: 160 },
  Active: {
    label: 'Durum',
    type: 'select',
    width: 120,
    options: [
      { value: 1, label: 'Aktif', labelKey: 'employees.status.active' },
      { value: 0, label: 'İnaktif', labelKey: 'employees.status.inactive' },
    ],
  },
  employeeExitDate: { label: 'İşten Ayrılma Tarihi', type: 'date', width: 180 },
  address: { label: 'Ev Adresi', type: 'text', width: 260 },
  workAddress: { label: 'İş Adresi', type: 'text', width: 260 },
  workCity: { label: 'Çalıştığı İl', type: 'text', width: 140 },
  annualPermit: { label: 'Yıllık İzin', type: 'text', width: 120 },
  birthday: { label: 'Doğum Tarihi', type: 'date', width: 140 },
  isHandicappedPerson: { label: 'Engellilik Durumu', type: 'select', width: 170, options: YES_NO_OPTIONS },
  isIntern: { label: 'Stajyer', type: 'select', width: 120, options: YES_NO_OPTIONS },
  isRetired: { label: 'Emekli', type: 'select', width: 120, options: YES_NO_OPTIONS },
  languageInfo: { label: 'Dil Bilgisi', type: 'text', width: 200 },
  nationality: { label: 'Uyruk', type: 'text', width: 130 },
  educationInfo: {
    label: 'Eğitim Bilgisi',
    type: 'select',
    width: 220,
    options: [
      { value: 'Ortaokul / Ortaöğretim', label: 'Ortaokul / Ortaöğretim', labelKey: 'employees.options.education.secondary' },
      { value: 'Lise', label: 'Lise', labelKey: 'employees.options.education.highSchool' },
      { value: 'Ön Lisans (2 Yıllık Üniversite)', label: 'Ön Lisans (2 Yıllık Üniversite)', labelKey: 'employees.options.education.associate' },
      { value: 'Lisans (4 Yıllık Üniversite)', label: 'Lisans (4 Yıllık Üniversite)', labelKey: 'employees.options.education.bachelor' },
      { value: 'Yüksek Lisans (Master)', label: 'Yüksek Lisans (Master)', labelKey: 'employees.options.education.master' },
      { value: 'Doktora (PhD)', label: 'Doktora (PhD)', labelKey: 'employees.options.education.phd' },
    ],
  },
};

export const COLUMN_KEYS = Object.keys(EMPLOYEE_COLUMNS);

// İlk açılışta görünen sütunlar (geniş ekranı dolduracak kadar)
export const DEFAULT_VISIBLE_COLUMNS = {
  eName: true,
  eSurname: true,
  tcNumber: true,
  phone: true,
  eMail: true,
  departman: true,
  profession: true,
  workType: true,
  startDate: true,
  Active: true,
  employeeExitDate: true,
  address: false,
  workAddress: false,
  workCity: false,
  annualPermit: false,
  birthday: false,
  isHandicappedPerson: false,
  isIntern: false,
  isRetired: false,
  languageInfo: false,
  nationality: false,
  educationInfo: false,
};

export const MIN_COLUMN_WIDTH = 96;

// Kimlik sütunları: tablonun başındaysalar yatay kaydırmada sabit kalırlar.
export const PINNED_COLUMNS = ['eName', 'eSurname'];

// Aktif filtresi açıkken bu sütun anlamsız olduğundan tabloda gizlenir.
export const EXIT_DATE_COLUMN = 'employeeExitDate';

const EMPTY_MARKERS = ['nan', 'nat', 'n/a', 'null', 'undefined'];

export const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  Number.isNaN(value) ||
  (typeof value === 'string' && EMPTY_MARKERS.includes(value.toLowerCase()));

/** Boş değerler için '-', diğerleri metin olarak (tablo hücreleri CSS ile kısaltır). */
export const displayValue = (value) => (isEmptyValue(value) ? '-' : String(value));

/** Excel çıktısında kullanılan kısaltma (eski davranış korunur). */
export const truncateText = (text, maxLength = 15) => {
  if (isEmptyValue(text)) return '-';
  const textStr = String(text);
  return textStr.length > maxLength ? `${textStr.substring(0, maxLength)}...` : textStr;
};

// Tarih alanlarını güvenli şekilde biçimlendirmek için yardımcı fonksiyon
export const formatDate = (value, locale = 'tr-TR') => {
  if (!value) return '-';
  // Firestore tarihleri string (YYYY-MM-DD) veya Timestamp/ISO olabilir
  try {
    const date = new Date(value);
    if (isNaN(date)) return truncateText(value);
    return date.toLocaleDateString(locale);
  } catch (e) {
    return truncateText(value);
  }
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Tarih alanı (date input'tan gelen 'YYYY-MM-DD') gün.ay.yıl olarak; başka
 * biçimde kaydedilmiş eski değerler olduğu gibi gösterilir.
 */
export const formatIsoDate = (value, locale = 'tr-TR') => {
  if (isEmptyValue(value)) return '-';
  const match = typeof value === 'string' ? value.trim().match(ISO_DATE) : null;
  if (!match) return String(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(locale);
};

const hasFilterValue = (value) => value !== undefined && value !== null && String(value) !== '';

/**
 * Bir sütun filtresinin listeye gerçekten uygulanıp uygulanmadığı: durum
 * filtresi her değerde, diğerleri en az 3 karakterde devreye girer
 * (useEmployeeDirectory'deki filtreleme ile aynı kural).
 */
export const getFilterState = (key, value) => {
  if (!hasFilterValue(value)) return 'off';
  if (key === 'Active') return 'applied';
  return String(value).trim().length >= 3 ? 'applied' : 'pending';
};

/** Sütun başlığı (geçerli dilde). */
export const columnLabel = (t, key) => t(`employees.columns.${key}`);

/** Bir seçeneğin geçerli dildeki adı. */
export const optionLabel = (t, option) => (option.labelKey ? t(option.labelKey) : option.label);

/** Seçimli sütunlarda bir değerin adı (çip, hücre); diğerlerinde değerin kendisi. */
export const getFilterValueLabel = (t, key, value) => {
  const option = EMPLOYEE_COLUMNS[key]?.options?.find((item) => String(item.value) === String(value));
  return option ? optionLabel(t, option) : String(value);
};

/** Evet/hayır alanları kayıtlarda true/false ya da 'Yes'/'No' olabilir. */
export const isYes = (value) => value === true || value === 'Yes';
